import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import '../styles/App.css';

interface Tool {
  id: string;
  title: string;
  description: string;
  icon: string;
  path: string;
  category: string;
}

const tools: Tool[] = [
  {
    id: 'json-formatter',
    title: 'JSON Formatter',
    description: 'Format, validate and beautify JSON data',
    icon: '{}',
    path: '/json-formatter',
    category: 'Formatter'
  },
  {
    id: 'jwt-decoder',
    title: 'JWT Decoder',
    description: 'Decode and analyze JWT tokens',
    icon: '🔐',
    path: '/jwt-decoder',
    category: 'Security'
  },
  {
    id: 'base64-encoder',
    title: 'Base64 Encoder',
    description: 'Encode and decode text using Base64',
    icon: '🔤',
    path: '/base64',
    category: 'Encoder'
  },
  {
    id: 'xml-formatter',
    title: 'XML Formatter',
    description: 'Format and beautify XML documents',
    icon: '</>',
    path: '/xml-formatter',
    category: 'Formatter'
  },
  {
    id: 'url-encoder',
    title: 'URL Encoder',
    description: 'Encode and decode URLs and query strings',
    icon: '🌐',
    path: '/url-encoder',
    category: 'Encoder'
  },
  {
    id: 'hash-generator',
    title: 'Hash Generator',
    description: 'Generate MD5, SHA-1, SHA-256 and more',
    icon: '#️⃣',
    path: '/hash-generator',
    category: 'Security'
  },
  {
    id: 'qr-generator',
    title: 'QR Code Generator',
    description: 'Create QR codes from text and URLs',
    icon: '⬛',
    path: '/qr-generator',
    category: 'Generator'
  },
  {
    id: 'color-picker',
    title: 'Color Picker',
    description: 'Convert between HEX, RGB, and HSL',
    icon: '🎨',
    path: '/color-picker',
    category: 'Utility'
  },
  {
    id: 'regex-tester',
    title: 'Regex Tester',
    description: 'Test and debug regular expressions',
    icon: '.*',
    path: '/regex-tester',
    category: 'Utility'
  },
  {
    id: 'uuid-generator',
    title: 'UUID Generator',
    description: 'Generate UUIDs with multiple version support',
    icon: '🆔',
    path: '/uuid-generator',
    category: 'Generator'
  },
  {
    id: 'base64-image',
    title: 'Base64 Image Converter',
    description: 'Convert images to Base64 and decode Base64 to images',
    icon: '🖼️',
    path: '/base64-image',
    category: 'Converter'
  },
  {
    id: 'timestamp-converter',
    title: 'Timestamp Converter',
    description: 'Convert between Unix timestamps, ISO dates, and readable formats',
    icon: '🕒',
    path: '/timestamp-converter',
    category: 'Converter'
  },
  {
    id: 'url-analyzer',
    title: 'URL Analyzer',
    description: 'Analyze URLs and extract information',
    icon: '🌐',
    path: '/url-analyzer',
    category: 'Analyzer'
  },
  {
    id: 'text-diff-checker',
    title: 'Text Diff Checker',
    description: 'So sánh 2 đoạn text và highlight sự khác biệt',
    icon: '📊',
    path: '/text-diff-checker',
    category: 'Text Tools'
  },
  {
    id: 'case-converter',
    title: 'Case Converter',
    description: 'Chuyển đổi kiểu viết text (camelCase, snake_case, v.v.)',
    icon: '🔤',
    path: '/case-converter',
    category: 'Text Tools'
  },
  {
    id: 'text-statistics',
    title: 'Text Statistics',
    description: 'Thống kê chi tiết về văn bản: đếm từ/ký tự/dòng, ước tính thời gian đọc',
    icon: '📊',
    path: '/text-statistics',
    category: 'Text Tools'
  },
  {
    id: 'json-path-tester',
    title: 'JSON Path Tester',
    description: 'Kiểm tra và đánh giá đường dẫn JSON',
    icon: '📊',
    path: '/json-path-tester',
    category: 'Text Tools'
  },
  {
    id: 'lorem-ipsum-generator',
    title: 'Lorem Ipsum Generator',
    description: 'Tạo placeholder text cho mockups/designs với nhiều phong cách',
    icon: '📝',
    path: '/lorem-ipsum-generator',
    category: 'Generator'
  }
];

const Dashboard: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const categories = ['All', ...Array.from(new Set(tools.map(tool => tool.category)))];

  const filteredTools = tools.filter(tool => {
    const matchesSearch = tool.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         tool.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || tool.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Add structured data for SEO
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Developer Tools",
      "description": "Bộ công cụ trực tuyến miễn phí cho lập trình viên: JSON formatter, JWT decoder, Base64 encoder, và nhiều hơn nữa",
      "url": "https://devtools.eduone.asia",
      "applicationCategory": "DeveloperApplication",
      "operatingSystem": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "1250"
      },
      "featureList": tools.map(tool => tool.title).join(', ')
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="dashboard">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div className="header-actions">
            <h1 className="dashboard-title">Developer Tools</h1>
            <button onClick={toggleTheme} className="theme-toggle" aria-label="Toggle theme">
              {theme === 'light' ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="5"/>
                  <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42m12.72-12.72l1.42-1.42"/>
                </svg>
              )}
            </button>
          </div>
          <p className="dashboard-subtitle">
            Essential tools for modern developers - Format, encode, decode and more
          </p>

          <div className="search-section">
            <div className="search-box">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Search tools..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="category-filters">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`category-button ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="tools-grid">
          {filteredTools.map((tool) => (
            <Link
              key={tool.id}
              to={tool.path}
              className="tool-card"
            >
              <div className="tool-card-header">
                <div className="tool-icon">{tool.icon}</div>
                <span className="tool-category">{tool.category}</span>
              </div>
              <h3 className="tool-card-title">{tool.title}</h3>
              <p className="tool-card-description">{tool.description}</p>
              <div className="tool-card-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14m-7-7 7 7-7 7"/>
                </svg>
              </div>
            </Link>
          ))}
        </div>

        {filteredTools.length === 0 && (
          <div className="no-results">
            <p>No tools found matching your criteria</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;

